import React, { useState, useEffect } from "react";
import Select from "react-select";
import { useNavigate, useParams } from "react-router-dom";
import AdminLayout from "../../layout/AdminLayout";
import { getOwners } from "../../../api/vehicle.api";
import { baseURL } from "../../../api/api";
import {
  addRestaurant,
  getRestaurantById,
  updateRestaurant,
} from "../../../api/restaurants.api";
import { errorMsg, success } from "../../../utils/notifications";

const initialData = {
  name: "",
  location: "",
  cuisineType: "Other",
  seatingCapacity: "",
  openingHours: {
    monday: { start: "", end: "" },
    tuesday: { start: "", end: "" },
    wednesday: { start: "", end: "" },
    thursday: { start: "", end: "" },
    friday: { start: "", end: "" },
    saturday: { start: "", end: "" },
    sunday: { start: "", end: "" },
  },
  footTrafficEstimate: "",
  description: "",
  contactInformation: {
    phone: "",
    email: "",
  },
  images: [],
  owner: "",
};

function AddRestaurants() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [addRestaurantData, setAddRestaurantData] = useState(initialData);
  const [owners, setOwners] = useState([]);
  const [saveRestaurantLoading, setSaveRestaurantLoading] = useState(false);
  const locations = ["Paris, France", "Douala, Cameroon", "Yaoundé, Cameroon"];
  const cuisineTypes = [
    "Fine Dining",
    "Casual Dining",
    "Fast Food",
    "Cafe",
    "Other",
  ];
  useEffect(() => {
    if (id) {
      getRestaurantById(id)
        .then((res) => {
          setAddRestaurantData(res?.data);
        })
        .catch((error) => {
          console.error("Error fetching restaurant data:", error);
        });
    }

    getOwners()
      .then((res) => {
        const ownerOptions = res?.data?.owners.map((owner) => ({
          value: owner._id,
          label: owner.name,
        }));
        setOwners(ownerOptions);
      })
      .catch((error) => {
        console.error("Error fetching owners:", error);
      });
  }, [id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddRestaurantData({
      ...addRestaurantData,
      [name]: value,
    });
  };

  const handleOpeningHoursChange = (day, timeType, value) => {
    setAddRestaurantData((prevData) => ({
      ...prevData,
      openingHours: {
        ...prevData.openingHours,
        [day]: {
          ...prevData.openingHours[day],
          [timeType]: value,
        },
      },
    }));
  };

  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files); // Get the files from the input

    // Ensure the total number of images is between 1 and 5
    if (files.length + addRestaurantData.images.length > 5) {
      errorMsg("You can only upload a maximum of 5 images.");
      return;
    }

    // Ensure the files are valid image files
    const validFiles = files.filter((file) => file.type.startsWith("image/"));
    if (validFiles.length === 0) {
      errorMsg("Please upload valid image files.");
      return;
    }

    // Update the restaurant data with file objects
    setAddRestaurantData((prevData) => ({
      ...prevData,
      images: [...prevData.images, ...validFiles],
    }));
  };

  const removeImage = (index) => {
    setAddRestaurantData((prevData) => ({
      ...prevData,
      images: prevData.images.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaveRestaurantLoading(true);

    const formData = new FormData();

    // Append restaurant fields to FormData
    formData.append("name", addRestaurantData.name);
    formData.append("location", addRestaurantData.location);
    formData.append("status", addRestaurantData.status);
    formData.append("cuisineType", addRestaurantData.cuisineType);
    formData.append("seatingCapacity", addRestaurantData.seatingCapacity);
    formData.append(
      "footTrafficEstimate",
      addRestaurantData.footTrafficEstimate
    );
    formData.append("description", addRestaurantData.description);
    formData.append(
      "contactInformation[phone]",
      addRestaurantData.contactInformation.phone
    );
    formData.append(
      "contactInformation[email]",
      addRestaurantData.contactInformation.email
    );
    formData.append("owner", addRestaurantData.owner);

    // Append opening hours to FormData
    Object.keys(addRestaurantData.openingHours).forEach((day) => {
      formData.append(
        `openingHours[${day}][start]`,
        addRestaurantData.openingHours[day].start
      );
      formData.append(
        `openingHours[${day}][end]`,
        addRestaurantData.openingHours[day].end
      );
    });

    // Separate existing images (URLs) and new images (Files)
    const existingImages = addRestaurantData.images.filter(
      (image) => typeof image === "string" // URLs for existing images
    );
    const newImages = addRestaurantData.images.filter(
      (image) => image instanceof File // File objects for new images
    );

    // Append existing images as an array of URLs
    existingImages.forEach((image, index) => {
      formData.append(`existingImages[${index}]`, image);
    });

    // Append new images to FormData
    newImages.forEach((image) => {
      formData.append("images", image);
    });

    try {
      if (id) {
        await updateRestaurant(id, formData); // Ensure `updateRestaurant` handles both existing and new images
        success("Fixed Location updated successfully");
        navigate(`/fixedLocations/${id}`);
      } else {
        await addRestaurant(formData); // Ensure `addRestaurant` handles `FormData`
        success("Fixed Location added successfully");
        navigate("/fixedLocations");
      }
    } catch (error) {
      errorMsg(error?.response?.data?.message || "Something went wrong!");
    } finally {
      setSaveRestaurantLoading(false);
    }
  };

  return (
    <AdminLayout>
      <div className="container mx-auto px-4">
        <h1 className="text-2xl font-semibold text-gray-700 mb-6 ms-2">
          {id ? "Update Fixed Location" : "Add New Fixed Location"}
        </h1>
        <form
          onSubmit={handleSubmit}
          className="bg-white shadow rounded-lg p-8"
        >
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {/* Restaurant Name */}
            <div className="col-span-1">
              <label
                htmlFor="name"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Fixed Location Name
              </label>
              <input
                type="text"
                name="name"
                value={addRestaurantData.name}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                required
              />
            </div>

            {/* Location */}
            <div>
              <label
                htmlFor="location"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Location
              </label>
              <select
                name="location"
                id="location"
                value={addRestaurantData.location}
                onChange={handleChange}
                required
                className="w-full p-2 border rounded"
              >
                <option value="">Select a location</option>
                {locations.map((location) => (
                  <option key={location} value={location}>
                    {location}
                  </option>
                ))}
              </select>
            </div>

            {/* Status */}
            <div>
              <label
                htmlFor="status"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Status
              </label>
              <select
                name="status"
                id="status"
                value={addRestaurantData.status}
                onChange={handleChange}
                required
                className="w-full p-2 border rounded"
              >
                <option value="">Select a status</option>
                <option value="Pending Approval">Pending Approval</option>
                <option value="Approved">Approved</option>
              </select>
            </div>

            {/* Cuisine Type */}
            <div>
              <label
                htmlFor="cuisineType"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Cuisine Type
              </label>
              <select
                name="cuisineType"
                id="cuisineType"
                value={addRestaurantData.cuisineType}
                onChange={handleChange}
                required
                className="w-full p-2 border rounded"
              >
                {cuisineTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            {/* Owner Field */}
            <div>
              <label
                htmlFor="owner"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Owner
              </label>
              <Select
                name="owner"
                id="owner"
                options={owners}
                value={owners.find(
                  (owner) => owner.value === addRestaurantData.owner
                )}
                onChange={(selectedOption) =>
                  setAddRestaurantData({
                    ...addRestaurantData,
                    owner: selectedOption?.value,
                  })
                }
                className="w-full"
                placeholder="Select an owner"
                required
              />
            </div>

            {/* Seating Capacity */}
            <div className="col-span-1">
              <label
                htmlFor="seatingCapacity"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Seating Capacity
              </label>
              <input
                type="number"
                name="seatingCapacity"
                value={addRestaurantData.seatingCapacity}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                required
              />
            </div>

            {/* Foot Traffic Estimate */}
            <div className="col-span-1">
              <label
                htmlFor="footTrafficEstimate"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Foot Traffic Estimate
              </label>
              <input
                type="number"
                name="footTrafficEstimate"
                value={addRestaurantData.footTrafficEstimate}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
              />
            </div>
            {/* Contact Information - Phone */}
            <div className="col-span-1">
              <label
                htmlFor="contactPhone"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Phone
              </label>
              <input
                type="tel"
                name="phone"
                value={addRestaurantData.contactInformation.phone}
                onChange={(e) =>
                  setAddRestaurantData({
                    ...addRestaurantData,
                    contactInformation: {
                      ...addRestaurantData.contactInformation,
                      phone: e.target.value,
                    },
                  })
                }
                className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="Enter phone number"
                required
              />
            </div>

            {/* Contact Information - Email */}
            <div className="col-span-1">
              <label
                htmlFor="contactEmail"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Email
              </label>
              <input
                type="email"
                name="email"
                value={addRestaurantData.contactInformation.email}
                onChange={(e) =>
                  setAddRestaurantData({
                    ...addRestaurantData,
                    contactInformation: {
                      ...addRestaurantData.contactInformation,
                      email: e.target.value,
                    },
                  })
                }
                className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                placeholder="Enter email address"
                required
              />
            </div>

            {/* Description */}
            <div className="col-span-2">
              <label
                htmlFor="description"
                className="block text-sm font-semibold text-gray-600 mb-2"
              >
                Description
              </label>
              <textarea
                name="description"
                value={addRestaurantData.description}
                onChange={handleChange}
                className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                rows="1"
              />
            </div>
          </div>
          <div className="col-span-3">
            <label className="block text-sm font-semibold text-gray-600 mb-2">
              Upload Images (Max: 5)
            </label>

            {/* Custom file input */}
            <div className="relative flex justify-center items-center w-full h-32 border rounded-md cursor-pointer bg-gray-100">
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageUpload}
                className="absolute inset-0 opacity-0 cursor-pointer"
              />
              <span className="text-4xl text-gray-500">+</span>
            </div>

            {/* Show selected images */}
            <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
              {addRestaurantData.images.map((image, index) => (
                <div key={index} className="relative group">
                  {image && (
                    <img
                      // Check if the image is a URL or File object
                      src={
                        typeof image === "string"
                          ? `${baseURL}/s3/images/${image}` // Construct the full S3 URL for each image
                          : URL.createObjectURL(image) // Use object URL for File objects (newly uploaded images)
                      }
                      alt={`Uploaded ${index + 1}`}
                      className="w-full h-32 object-cover rounded-md"
                    />
                  )}
                  {/* Remove button for each image */}
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute top-2 right-2 bg-red-500 text-white p-1 rounded-full opacity-75 group-hover:opacity-100"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
          {/* Opening Hours */}
          <div className="mt-6">
            <label className="block text-sm font-semibold text-gray-600 mb-4">
              Opening Hours
            </label>
            <div className="grid grid-cols-2 gap-4 md:grid-cols-4 lg:grid-cols-7">
              {Object.keys(addRestaurantData.openingHours).map((day) => (
                <div key={day} className="flex flex-col items-center">
                  <span className="font-medium text-gray-700 capitalize">
                    {day}
                  </span>
                  <input
                    type="time"
                    value={addRestaurantData.openingHours[day].start}
                    onChange={(e) =>
                      handleOpeningHoursChange(day, "start", e.target.value)
                    }
                    className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500"
                  />
                  <input
                    type="time"
                    value={addRestaurantData.openingHours[day].end}
                    onChange={(e) =>
                      handleOpeningHoursChange(day, "end", e.target.value)
                    }
                    className="w-full border rounded-md px-3 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-yellow-500 mt-2"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Submit Button */}
          <div className="mt-8">
            <button
              type="submit"
              className={`w-full sm:w-auto px-6 py-3 bg-yellow-500 text-black rounded-md font-semibold hover:bg-yellow-600 ${
                saveRestaurantLoading && "opacity-50 cursor-not-allowed"
              }`}
              disabled={saveRestaurantLoading}
            >
              {saveRestaurantLoading ? "Saving..." : "Save Fixed Location"}
            </button>
          </div>
        </form>
      </div>
    </AdminLayout>
  );
}

export default AddRestaurants;
